import { useCallback, useEffect, useMemo, useState } from 'react';
import { transformDataFromHoaDon, transformDataFromHoaDonDichVu, type HoaDonSourceData } from '../utils/transform-data';
import { GiayBaoNo, type TaiKhoan } from '@/types/schemas';
import { useAuth } from '@/contexts/auth-context';
import { useSearchParams } from '@/routing';
import { QUERY_KEYS } from '@/constants';
import { useCRUD } from '@/hooks';
import api from '@/lib/api';

export function useEditGiayBaoNoWithUpdatedInvoice() {
  const searchParams = useSearchParams();
  const { entity } = useAuth();

  const editUuid = searchParams.get('edit');
  const hdmhUuid = searchParams.get('hdmh');
  const hdmdvUuid = searchParams.get('hdmdv');

  const shouldEditWithUpdatedData = Boolean(editUuid && (hdmhUuid || hdmdvUuid));

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const taiKhoanSearchData = useMemo(() => ({ prefix: '1121' }), []);
  const { data: taiKhoan, isLoading: isLoadingTaiKhoan } = useCRUD<TaiKhoan, unknown>({
    endpoint: QUERY_KEYS.TAI_KHOAN,
    searchData: taiKhoanSearchData
  });

  const fetchInvoiceByUuid = useCallback(
    async (uuid: string, isHoaDonMuaHang: boolean = true): Promise<HoaDonSourceData | null> => {
      if (!entity?.slug) return null;

      try {
        const cacheKey = `hoa_don_detail_${uuid}`;
        const cachedData = sessionStorage.getItem(cacheKey);
        if (cachedData) {
          sessionStorage.removeItem(cacheKey);
          return JSON.parse(cachedData) as HoaDonSourceData;
        }
        const endpoint = isHoaDonMuaHang ? QUERY_KEYS.HOA_DON_MUA_HANG_TRONG_NUOC : QUERY_KEYS.HOA_DON_MUA_DICH_VU;
        const response = await api.get(`/entities/${entity.slug}/erp/${endpoint}/${uuid}/`);
        return response.data as HoaDonSourceData;
      } catch (error) {
        return null;
      }
    },
    [entity?.slug]
  );

  const [mergedFormData, setMergedFormData] = useState<GiayBaoNo | null>(null);

  const fetchGiayBaoNoByUuid = useCallback(
    async (uuid: string): Promise<GiayBaoNo | null> => {
      if (!entity?.slug) return null;

      try {
        const response = await api.get(`/entities/${entity.slug}/erp/${QUERY_KEYS.GIAY_BAO_NO}/${uuid}/`);
        return response.data as GiayBaoNo;
      } catch (error) {
        return null;
      }
    },
    [entity?.slug]
  );

  const updateGiayBaoNoWithInvoice = useCallback(
    (existingGiayBaoNo: GiayBaoNo | null, updatedInvoiceData: HoaDonSourceData | null): GiayBaoNo | null => {
      if (!existingGiayBaoNo || !updatedInvoiceData) {
        return null;
      }

      let transformedFromInvoice = null;
      if (hdmhUuid) {
        transformedFromInvoice = transformDataFromHoaDon(updatedInvoiceData);
      } else if (hdmdvUuid) {
        transformedFromInvoice = transformDataFromHoaDonDichVu(updatedInvoiceData);
      }

      if (!transformedFromInvoice) {
        return existingGiayBaoNo;
      }

      return {
        ...existingGiayBaoNo,

        ngay_ct0: updatedInvoiceData.ngay_ct,
        so_ct0: updatedInvoiceData.so_ct,
        dien_giai: updatedInvoiceData.dien_giai,

        ma_kh: updatedInvoiceData.ma_kh_data?.uuid || updatedInvoiceData.ma_kh,
        ong_ba: updatedInvoiceData.ong_ba,
        dia_chi: updatedInvoiceData.ma_kh_data?.address || updatedInvoiceData.dia_chi,

        ma_nt: updatedInvoiceData.ma_nt || 'VND',
        ty_gia: updatedInvoiceData.ty_gia || 1,

        t_tien_nt: updatedInvoiceData.t_tt || 0,
        t_thue_nt: updatedInvoiceData.t_thue_nt || updatedInvoiceData.t_thue || 0,
        t_cp_nt: updatedInvoiceData.t_cp_nt || updatedInvoiceData.t_cp || 0,
        t_tt_nt: updatedInvoiceData.t_tt || 0,

        chi_tiet_data: transformedFromInvoice.chi_tiet_data || existingGiayBaoNo.chi_tiet_data
      };
    },
    [hdmhUuid, hdmdvUuid]
  );

  useEffect(() => {
    const invoiceUuid = hdmdvUuid || hdmhUuid;
    const isHoaDonMuaHang = !!hdmhUuid;

    if (editUuid && invoiceUuid) {
      setIsLoading(true);
      setError(null);

      Promise.all([fetchGiayBaoNoByUuid(editUuid), fetchInvoiceByUuid(invoiceUuid, isHoaDonMuaHang)])
        .then(([giayBaoNoData, updatedInvoiceData]) => {
          const merged = updateGiayBaoNoWithInvoice(giayBaoNoData, updatedInvoiceData);
          if (merged && !isLoadingTaiKhoan && taiKhoan && taiKhoan.length > 0) {
            merged.tk_data = taiKhoan[0];
          }
          setMergedFormData(merged);
        })
        .catch(() => {
          setError('Không thể tải dữ liệu giấy báo nợ hoặc hóa đơn');
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setMergedFormData(null);
      setError(null);
    }
  }, [
    editUuid,
    hdmhUuid,
    hdmdvUuid,
    fetchGiayBaoNoByUuid,
    fetchInvoiceByUuid,
    updateGiayBaoNoWithInvoice,
    isLoadingTaiKhoan,
    taiKhoan,
    shouldEditWithUpdatedData
  ]);

  return {
    shouldEditWithUpdatedData,
    mergedFormData,
    editGiayBaoNoUuid: editUuid,
    isLoading,
    error
  };
}
